import { Alert, Button, Checkbox, Col, Form, Input, message, Modal, Row, Space } from 'antd';
import { AppCopyright, AppLogo, Icon } from 'components';
import loginFasooSSO from 'pages/config/vendor-supplied-js/fsw_weblogin';
import { DownloadTable } from 'pages/help/Download';
import { getXauthFromCookie } from 'pages/utils';
import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineLock, AiOutlineUser } from 'react-icons/ai';
import {
  accountLogin,
  doAutoLogin,
  encryptRSA,
  getCustomRedirect,
  getRSAKey,
  requestResetPassword,
} from 'services/auth';
import LoginMenuContext from 'store/LoginMenuContext';
import ThemeContext from 'store/ThemeContext';
import styled from 'styled-components';
import packageJson from '../../../package.json';
import locales from '../../locales';
import { TwoFactor } from './components';
import ResetPassword from './ResetPassword';

const styles = {
  formHeight: {
    height: 40,
  },
};

const StyledLogin = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #ecf1f6;
  .loginBox {
    width: 368px;
    margin: 0 auto;
    padding: 40px;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 1px 1px 1px rgba(0, 0, 0, 0.07), 0 3px 4px -1px rgba(0, 0, 0, 0.07),
      0 4px 9px 2px rgba(0, 0, 0, 0.07);
  }
`;

function Login({ history, location }) {
  const [error, setError] = useState(undefined);
  const [visibleResetPw, setVisibleResetPw] = useState(false);
  const [user, setUser] = useState();
  const [customUrls, setCustomUrls] = useState();
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [serviceGuideLink, setServiceGuideLink] = useState(null);
  const [inputIDForm] = Form.useForm();
  const hideMenus = useContext(LoginMenuContext);
  const theme = useContext(ThemeContext);
  const [checked, setChecked] = useState(false);
  const [twoFactorChannel, setTwoFactorChannel] = useState([]); // two factor 채널 정보 [{ label: '문자', value: 'sms' },{ label: '이메일', value: 'email' }]
  const [twoFactorAuthenticated, setTwoFactorAuthenticated] = useState(false); // two factor이 되었는지 여부를 의미, 인증 후 처리(postAuthentication)를 위함
  const [loginInfo, setLoginInfo] = useState(undefined); // 로그인 정보를 의미하며 인증 후 처리(postAuthentication)에서 사용
  const [btnLoading, setBtnLoading] = useState(false);
  const [encryptKey, setEncryptKey] = useState();

  useEffect(() => {
    if (theme?.autoLogin?.checked) {
      setChecked(true);
    }

    const tryAutoLogin = async () => {
      if (localStorage.getItem('autoLogin')) {
        const autoLogin = localStorage.getItem('autoLogin');
        const isAutoLogin = autoLogin === 'true';
        setChecked(isAutoLogin);
        const token1 = getXauthFromCookie();
        if (isAutoLogin && token1) {
          try {
            const res = await doAutoLogin(token1);

            if (res?.status === 201) {
              localStorage.setItem('autoConnect', true);
              localStorage.setItem('redirectComplete', false);

              history.push({
                pathname: '/desktops',
                redirectUrlToVd: location?.redirectUrlToVd,
              });
            }
          } catch (e) {
            // error handler에서 401 error는 그냥 throw 해준다.
            setError(e?.response?.data?.msg);
          }
        }
      }
    };

    tryAutoLogin();
  }, []);

  useEffect(() => {
    (async () => {
      const resource = await import(`download/${theme.serviceGuide.link}`);
      setServiceGuideLink(resource.default);
    })();

    return () => {
      // setLogo(null);
    };
  }, []);

  useEffect(() => {
    getCustomRedirect().then((customUrl) => setCustomUrls(customUrl));
    getRSAKey().then(({ data }) => setEncryptKey(data.key));
  }, []);

  // 로그인 submit
  const onFinish = async (credential) => {
    setBtnLoading(true);
    setError(undefined); // sumit 할때마다 error를 clear 한다.

    try {
      const password1 = encryptRSA(credential.password, encryptKey);
      const res = await accountLogin({
        ...credential,
        autoLogin: checked,
        password: password1,
      });

      /**
       * 로그인 후 처리: 비밀번호 재설정 | two factor
       * 주의: two factor 인지 먼저 판별(setTwoFactorChannel)한 후 loginInfo를 세팅해야 함
       */
      if (res.result) {
        // two -factor 인증(lgd)
        if (res.twoFactor) {
          if (res.twoFactor.sResultCode === '600') {
            localStorage.setItem('sTokenKey', res.twoFactor.userToken);

            const availableTwoFactorChanel = [
              { label: '문자', value: 'sms' },
              { label: '이메일', value: 'email' },
            ];
            setTwoFactorChannel(
              availableTwoFactorChanel.filter((item) => res.twoFactor?.method.includes(item.value)),
            );
          } else {
            message.error(res.twoFactor.sResultMsg);
            setBtnLoading(false);
            return;
          }
        }

        if (res.ldapCode === 773) {
          setVisibleResetPw(true);
          setUser(credential.userId);
        } else {
          setLoginInfo({
            userId: credential.userId,
            password: password1,
            username: res.username,
            autoLogin: checked,
            autoConnect: true,
            redirectComplete: false,
          });
        }
      } else {
        setError(res.msg);
      }
      setBtnLoading(false);
    } catch (e) {
      // error handler에서 401 error는 그냥 throw 해준다.
      setError(e?.response?.data?.msg);
      setBtnLoading(false);
    }
    setBtnLoading(false);
  };

  // 로그인했고 two factor가 아닌 경우 || 로그인했고 two factor 인증 완료된 경우
  useEffect(() => {
    if ((loginInfo && !twoFactorChannel.length) || (loginInfo && twoFactorAuthenticated)) {
      Object.entries(loginInfo).forEach(([key, value]) => localStorage.setItem(key, value));

      // token_key를 이용하여 fss_login
      if (twoFactorAuthenticated) {
        loginFasooSSO('0000000000013470', loginInfo.userId, localStorage.getItem('sTokenKey'), '');
      }

      history.push({
        pathname: '/auth/requiredApps',
        redirectUrlToVd: location?.redirectUrlToVd,
      });
    }
  }, [loginInfo, twoFactorAuthenticated]);

  // 비밀번호 변경 컴포넌트
  const InputForResetPWD = () => {
    const request = async (credential) => {
      const res = await requestResetPassword(credential);
      Modal[res.type]({
        centered: true,
        title: res.message,
        content: res.description,
      });
    };

    Modal.confirm({
      title: '비밀번호 재설정',
      cancelText: '취소',
      okText: '이메일 발송',
      centered: true,
      content: (
        <div>
          <p>
            아이디를 입력하여 주십시오.
            <br />
            아이디에 등록된 이메일로 재설정 할 수 있는 주소를 보내드립니다.
          </p>
          <Form form={inputIDForm}>
            <Form.Item
              label="아이디"
              name="id"
              rules={[{ required: true, message: '아이디를 입력하세요.' }]}
              hasFeedback
            >
              <Input />
            </Form.Item>
          </Form>
        </div>
      ),
      onOk(doClose) {
        inputIDForm
          .validateFields()
          .then((values) => {
            request(values.id);
            inputIDForm.resetFields();
            doClose();
          })
          .catch(() => {
            /* pass */
          });
      },
    });
  };

  return (
    <StyledLogin>
      <div className="loginBox">
        <Row gutter={[0, 16]}>
          <Col span={24} style={{ textAlign: 'center', marginBottom: 16 }}>
            <AppLogo />
          </Col>
          {error && (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                style={{ gap: 8 }}
                message={<div style={{ whiteSpace: 'pre-line' }}>{error}</div>}
              />
            </Col>
          )}
          <Col span={24}>
            {twoFactorChannel.length ? (
              <TwoFactor
                setError={setError}
                twoFactorChannel={twoFactorChannel}
                setTwoFactorAuthenticated={setTwoFactorAuthenticated}
                loginInfo={loginInfo}
              />
            ) : (
              <Form onFinish={onFinish} layout="vertical">
                <Row gutter={[0, 8]}>
                  <Col span={24}>
                    <Form.Item
                      name="userId"
                      rules={[
                        { required: true, message: locales['front.login.id.requireMessage'] },
                      ]}
                    >
                      <Input
                        prefix={<Icon name={<AiOutlineUser focusable={false} />} />}
                        placeholder={locales['front.login.id']}
                        style={styles.formHeight}
                        autoFocus
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: locales['front.login.password.requireMessage'] },
                      ]}
                    >
                      <Input.Password
                        visibilityToggle={false}
                        allowClear
                        prefix={<Icon name={<AiOutlineLock focusable={false} />} />}
                        placeholder={locales['front.login.password']}
                        style={styles.formHeight}
                      />
                    </Form.Item>
                  </Col>
                  {theme.autoLogin && (
                    <Col span={24}>
                      <Form.Item noStyle name="autoLogin">
                        <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
                          자동 로그인
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={24}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        block
                        style={styles.formHeight}
                        loading={btnLoading}
                      >
                        LOGIN
                      </Button>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Space direction="vertical" style={{ flexWrap: 'wrap', marginTop: 16 }}>
                      <Space style={{ flexWrap: 'wrap' }}>
                        {!hideMenus.includes('signup') && customUrls?.signUp && (
                          <Button
                            type="link"
                            onClick={() => {
                              window.open(
                                customUrls?.signUp,
                                '',
                                `${
                                  theme.customUrls?.signUp?.windowFeatures !== undefined
                                    ? theme.customUrls.signUp.windowFeatures
                                    : `height=${window.screen.height},width=${window.screen.width},resizable=yes,fullscreen=yes`
                                }`,
                              );
                            }}
                          >
                            ID 등록
                          </Button>
                        )}
                        <Button
                          type="link"
                          style={{ padding: 0 }}
                          onClick={
                            customUrls?.passwordReset
                              ? () => {
                                  window.open(
                                    customUrls?.passwordReset,
                                    '',
                                    `${
                                      theme.customUrls?.passwordReset?.windowFeatures !== undefined
                                        ? theme.customUrls.passwordReset.windowFeatures
                                        : `height=${window.screen.height},width=${window.screen.width},resizable=yes,fullscreen=yes`
                                    }`,
                                  );
                                }
                              : InputForResetPWD
                          }
                        >
                          비밀번호 초기화하기
                        </Button>

                        {/* FIXME: 하나은행에서 줄을 한줄로 합쳐 달라고 해서 할 수 없이 추가함 */}
                        {packageJson.config?.preid !== 'hanabank' && (
                          <div style={{ flex: 1, width: 100 }} />
                        )}

                        {!hideMenus.includes('guide') && (
                          <a href={serviceGuideLink} download className="ant-btn">
                            {theme.serviceGuide.text}
                          </a>
                        )}

                        {!hideMenus.includes('fileDownload') && (
                          <Button onClick={() => setDownloadModalVisible(true)}>
                            접속 프로그램 다운로드
                          </Button>
                        )}

                        <Modal
                          title="접속 프로그램 다운로드"
                          centered
                          width={720}
                          visible={downloadModalVisible}
                          onOk={() => setDownloadModalVisible(false)}
                          onCancel={() => setDownloadModalVisible(false)}
                          footer={[
                            <Button key="ok" onClick={() => setDownloadModalVisible(false)}>
                              확인
                            </Button>,
                          ]}
                        >
                          <DownloadTable size="small" />
                        </Modal>
                      </Space>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <AppCopyright />
      </div>
      <Modal
        visible={visibleResetPw}
        centered
        width={420}
        onCancel={() => {
          setVisibleResetPw(false);
        }}
        footer={[]}
      >
        <ResetPassword isAdOption user={user} />
      </Modal>
    </StyledLogin>
  );
}

export default Login;
